import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';

import { Explicit } from '../../components/ui/Explicit';
import { Drawer } from '../../app/Drawer';
import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { FavoriteButton } from '../../components/ui/FavoriteButton';
import { SkeletonBlock, SkeletonText } from '../../components/ui/Loader';
import { RepostButton } from '../../components/ui/RepostButton';
import { ApiError, api } from '../../lib/api';
import { type PlayableTrack, usePlayer } from '../../stores/player';
import { CommentThread } from '../comments/CommentThread';
import { Waveform } from '../player/Waveform';
import { useDocumentTitle } from '../../lib/title';

interface DrawerTrack extends PlayableTrack {
  description: string | null;
  status: string;
  explicit: boolean;
  play_count: number;
  like_count: number;
  favorited: boolean;
  reposted: boolean;
  repost_count: number;
  artwork_small: string | null;
}

/**
 * A track, in the side drawer.
 *
 * ## Its own composition, not the page squeezed into 560px
 *
 * `TrackPage` is a wide two-column layout with an editor, a credits table and a
 * report control. Rendering it in a panel a third of the width would produce a
 * worse version of both. So this is the part somebody actually opened a track
 * for - hear it, see what it is, say something - and the header carries a link
 * to the page for everything else.
 *
 * What it does *not* do is reimplement any of that. The waveform, the favourite
 * and repost buttons and the whole comment thread are the same components the
 * page uses, so a fix to any of them lands in both.
 */
export function TrackDrawer() {
  const { id = '' } = useParams();

  const current = usePlayer((s) => s.track);
  const playing = usePlayer((s) => s.playing);
  const position = usePlayer((s) => s.position);
  const duration = usePlayer((s) => s.duration);
  const play = usePlayer((s) => s.play);
  const toggle = usePlayer((s) => s.toggle);
  const seek = usePlayer((s) => s.seek);

  const { data, isPending, error } = useQuery({
    queryKey: ['track', id],
    queryFn: () => api.get<DrawerTrack>(`/tracks/${id}`),
    retry: false,
  });

  useDocumentTitle(data?.title ?? null);

  const isCurrent = current?.id === data?.id;
  const total = isCurrent ? duration || (data?.duration_ms ?? 0) / 1000 : (data?.duration_ms ?? 0) / 1000;

  return (
    <Drawer title={data?.title ?? 'Track'} fullPath={`/tracks/${id}`}>
      {isPending && (
        <div className="flex flex-col gap-5" aria-busy="true">
          <SkeletonBlock className="aspect-square w-full" />
          <SkeletonText lines={2} />
        </div>
      )}

      {error && (
        <Alert tone="error">
          {error instanceof ApiError ? error.message : 'That track could not be loaded.'}
        </Alert>
      )}

      {data && (
        <div className="flex flex-col gap-5">
          {(data.artwork ?? data.artwork_small) ? (
            <img
              src={data.artwork ?? data.artwork_small ?? undefined}
              alt=""
              className="aspect-square w-full rounded-[var(--radius-panel)] object-cover"
            />
          ) : (
            <div
              aria-hidden
              className="aspect-square w-full rounded-[var(--radius-panel)]"
              style={{
                backgroundImage: 'repeating-linear-gradient(45deg,var(--color-blank) 0 14px,var(--color-blank-alt) 14px 28px)',
              }}
            />
          )}

          <div className="flex flex-col gap-1.5">
            <h2 className="flex items-center gap-2 text-2xl font-bold leading-tight tracking-tight text-ink">
              {data.explicit && <Explicit className="h-5 w-5 text-[13px]" />}
              {data.title}
            </h2>
            {data.owner && (
              <Link
                to={`/${data.owner.username}`}
                className="text-[15px] text-text-2 hover:text-ink hover:underline"
              >
                {data.owner.display_name}
              </Link>
            )}
            <span className="meta text-faint">
              {data.play_count.toLocaleString()} {data.play_count === 1 ? 'play' : 'plays'}
            </span>
          </div>

          {data.status === 'ready' ? (
            <>
              <Waveform
                url={data.waveform_url}
                progress={isCurrent && total > 0 ? Math.min(1, position / total) : 0}
                height={56}
                onSeek={isCurrent ? (fraction) => seek(fraction * total) : undefined}
              />

              <div className="flex flex-wrap items-center gap-2.5">
                <Button
                  onClick={() => (isCurrent ? toggle() : void play(toPlayable(data), undefined, 'track'))}
                  data-drawer-play
                >
                  {isCurrent && playing ? 'Pause' : 'Play'}
                </Button>

                <FavoriteButton
                  trackId={data.id}
                  favorited={data.favorited}
                  likeCount={data.like_count}
                  showCount
                  size="button"
                  className="rounded-[var(--radius-pill)] border border-line px-4 py-2.5"
                />

                <RepostButton
                  title={data.title}
                  artist={data.owner?.display_name ?? null}
                  trackId={data.id}
                  reposted={data.reposted}
                  repostCount={data.repost_count}
                  showCount
                />
              </div>
            </>
          ) : (
            // The owner's own unfinished upload. Everybody else never sees a
            // track in this state, because it is not public until it is ready.
            <Alert tone="info">This track is still being processed.</Alert>
          )}

          {data.description && (
            <p className="whitespace-pre-wrap text-[15px] leading-relaxed text-text-2">
              {data.description}
            </p>
          )}

          {data.status === 'ready' && (
            <div className="border-t border-line pt-5">
              <CommentThread trackId={data.id} />
            </div>
          )}
        </div>
      )}
    </Drawer>
  );
}

function toPlayable(track: DrawerTrack): PlayableTrack {
  return {
    id: track.id,
    title: track.title,
    duration_ms: track.duration_ms,
    waveform_url: track.waveform_url,
    artwork: track.artwork,
    artwork_small: track.artwork_small,
    owner: track.owner,
    favorited: track.favorited,
    like_count: track.like_count,
  };
}
